// src/context/ActivationsContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import api from "../api/api";
import { withRetry } from "../utils/retry";

const ActivationsContext = createContext(null);

// BuyNumbers needs fast OTP updates, so this stays at 5s —
// but now there is only ONE 5s poll no matter how many pages use it.
const POLL_INTERVAL = 8000;

export function ActivationsProvider({ children }) {
  const [activations, setActivations] = useState([]);
  const [activationsLoading, setActivationsLoading] = useState(true);
  const isMounted = useRef(true);

  const refreshActivations = useCallback(async () => {
    try {
      const res = await withRetry(() => api.get("/user/activations/my"));
      if (isMounted.current) {
        const data = Array.isArray(res.data)
          ? res.data
          : res.data?.data || [];
        setActivations(data);
      }
    } catch (err) {
      // Silent on background poll — keep last known data, don't spam the user
      console.error("Activations refresh failed:", err?.message);
    } finally {
      if (isMounted.current) setActivationsLoading(false);
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;

    const token = localStorage.getItem("accessToken");
    if (!token) {
      setActivationsLoading(false);
      return;
    }

    refreshActivations();
    const interval = setInterval(refreshActivations, POLL_INTERVAL);

    return () => {
      isMounted.current = false;
      clearInterval(interval);
    };
  }, [refreshActivations]);

  return (
    <ActivationsContext.Provider
      value={{ activations, activationsLoading, refreshActivations }}
    >
      {children}
    </ActivationsContext.Provider>
  );
}

export function useActivations() {
  const ctx = useContext(ActivationsContext);
  if (!ctx) {
    throw new Error(
      "useActivations must be used inside an ActivationsProvider"
    );
  }
  return ctx;
}